import { validate } from "uuid";
import { displayCategoryName, displayName, getEvidenceName } from "~/common/functions";
import type { OrderBy, RecordsBoolExp, RecordsOrderBy } from "~/common/types/graphql";
import type { GetEntitiesQueryVariables, GetEntityDetailGeoItemsQuery } from "~/features/entities/gqls";
import type {
  EntityCategoryChip,
  EntityChangeRequestRow,
  EntityDetailRaw,
  EntityDetailUI,
  EntityEvidenceRow,
  EntityGeoItemRow,
  EntityLineage,
  EntityListFilters,
  EntityPropertyGroup,
  EntityRaw,
  EntityRelationLifecycle,
  EntityRelationLifecycleRaw,
  EntityRelationLifecycleRow,
  EntityRelationRow,
  EntityRow,
  EntitySortOrder,
  LineageEdge,
  LineageEdgeType,
  LineageNode,
  RelatedCompoundEntity,
  RelatedCompoundEntityRaw,
  RelatedEntityLink,
  SortDirection,
} from "~/features/entities/types";
import {
  type EffectiveRecordPropertySchemasByCategory,
  requireEffectiveRecordSchemas,
} from "~/features/recordDetails/utils/effective-property-schemas";
import { geoItemDisplayName, geoItemLinkHeading, geoItemPlaceName } from "~/features/recordGeoItems/utils/geoItemLabel";
import type { Locale } from "~/i18n/resolveLocale";
import { parseGdtfRange } from "~/utils/gdtfRange";

const dedupeById = <T extends { id: string }>(items: readonly T[]): T[] => {
  const map = new Map<string, T>();
  for (const item of items) if (!map.has(item.id)) map.set(item.id, item);
  return [...map.values()];
};

const NOT_YET_APPLIED = new Set(["open", "approved"]);

type RecordWithGeoItems = {
  id: string;
  recordGeoItems: ReadonlyArray<{ geoItemId: string; date?: string | null }>;
};

export function toFirstGeoItemIdByRecordId(records: readonly RecordWithGeoItems[]): ReadonlyMap<string, string> {
  return new Map(
    records.flatMap(({ id, recordGeoItems }) => {
      const first = recordGeoItems.reduce<(typeof recordGeoItems)[number] | null>((current, candidate) => {
        if (!current) return candidate;
        const candidateDate = candidate.date ?? null;
        const currentDate = current.date ?? null;
        if (candidateDate === currentDate) {
          return candidate.geoItemId < current.geoItemId ? candidate : current;
        }
        if (candidateDate === null) return current;
        if (currentDate === null || candidateDate < currentDate) return candidate;
        return current;
      }, null);
      return first ? [[id, first.geoItemId] as const] : [];
    }),
  );
}

const formatDateRange = (date?: string | null, endDate?: string | null): string => {
  if (!date && !endDate) return "";
  if (date && endDate) return `${date} ~ ${endDate}`;
  return date ?? endDate ?? "";
};

type CategoryShape = { id: string; name: string; i18nLabel: unknown };

const toCategoryChip = (category: CategoryShape, locale: Locale): EntityCategoryChip => ({
  id: category.id,
  label: displayCategoryName(
    { name: category.name, i18nLabel: category.i18nLabel as Record<string, unknown> | null },
    locale,
  ),
});

const toCategoryChips = (
  metadataCategories: ReadonlyArray<{ category: CategoryShape }>,
  locale: Locale,
): EntityCategoryChip[] => metadataCategories.map((rc) => toCategoryChip(rc.category, locale));

function toEntityRow(raw: EntityRaw, locale: Locale, firstGeoItemIdByRecordId: ReadonlyMap<string, string>): EntityRow {
  return {
    id: raw.id,
    nameJa: raw.nameJa ?? "",
    dateRange: formatDateRange(raw.date, raw.endDate),
    updatedAt: raw.updatedAt,
    categories: toCategoryChips(raw.recordMetadata.recordMetadataCategories, locale),
    geoItemId: firstGeoItemIdByRecordId.get(raw.id) ?? null,
    isDeactivated: Boolean(raw.deactivatedAt),
  };
}

export function toEntityRowList(
  raws: readonly EntityRaw[],
  locale: Locale,
  firstGeoItemIdByRecordId: ReadonlyMap<string, string>,
): EntityRow[] {
  return raws.map((raw) => toEntityRow(raw, locale, firstGeoItemIdByRecordId));
}

export function toRelatedCompoundEntity(
  raw: RelatedCompoundEntityRaw | undefined,
  locale: Locale,
): RelatedCompoundEntity | null {
  if (!raw) return null;
  return {
    id: raw.id,
    key: raw.key,
    displayName: displayName({ name: raw.name, nameJa: raw.nameJa }, locale),
    version: raw.v,
  };
}

const toRelatedEntityLink = (
  entity:
    | {
        id: string;
        name?: string | null;
        nameJa?: string | null;
        recordMetadata: { recordMetadataCategories: ReadonlyArray<{ category: CategoryShape }> };
      }
    | null
    | undefined,
  locale: Locale,
): RelatedEntityLink | null => {
  if (!entity) return null;
  return {
    id: entity.id,
    displayName: displayName({ name: entity.name, nameJa: entity.nameJa }, locale),
    categories: toCategoryChips(entity.recordMetadata.recordMetadataCategories, locale),
  };
};

type SourceEdge = NonNullable<EntityDetailRaw["entityRecord"]>["sourceRecordEdges"][number];
type TargetEdge = NonNullable<EntityDetailRaw["entityRecord"]>["targetRecordEdges"][number];

const toSourceRelationRow = (edge: SourceEdge, locale: Locale): EntityRelationRow => ({
  relationId: edge.relationRecord.record.id,
  categories: toCategoryChips(edge.relationRecord.record.recordMetadata.recordMetadataCategories, locale),
  dateRange: formatDateRange(edge.relationRecord.record.date, edge.relationRecord.record.endDate),
  related: edge.relationRecord.targetRecordEdges
    .map((e) => toRelatedEntityLink(e.entity, locale))
    .filter((r): r is RelatedEntityLink => r !== null),
});

const toTargetRelationRow = (edge: TargetEdge, locale: Locale): EntityRelationRow => ({
  relationId: edge.relationRecord.record.id,
  categories: toCategoryChips(edge.relationRecord.record.recordMetadata.recordMetadataCategories, locale),
  dateRange: formatDateRange(edge.relationRecord.record.date, edge.relationRecord.record.endDate),
  related: edge.relationRecord.sourceRecordEdges
    .map((e) => toRelatedEntityLink(e.entity, locale))
    .filter((r): r is RelatedEntityLink => r !== null),
});

type EntityGeoItemRead = Extract<
  GetEntityDetailGeoItemsQuery["cnutGetPublishedRecordGeoItemsByRecord"],
  { __typename: "RecordGeoItemsByRecordOutput" }
>["recordGeoItems"][number];

export const toEntityGeoItemRows = (recordGeoItems: readonly EntityGeoItemRead[], locale: Locale): EntityGeoItemRow[] =>
  recordGeoItems.map((gi) => ({
    id: gi.geoItemId,
    label: geoItemDisplayName(gi.geoItemId, geoItemPlaceName(gi.geoItem?.i18nLabel, locale)),
    heading: geoItemLinkHeading(gi.i18nLabel, locale),
    date: gi.date ?? "",
    endDate: gi.endDate ?? "",
    countryIds: gi.countryCode ? [gi.countryCode] : [],
    deactivatedAt: gi.deactivatedAt ?? null,
    isDeactivated: Boolean(gi.deactivatedAt),
  }));

type MetadataCategory = EntityDetailRaw["recordMetadata"]["recordMetadataCategories"][number];

/** Property tables use the same effective schema contract as category detail. */
const toPropertyGroups = (
  metadataCategories: readonly MetadataCategory[],
  properties: Record<string, unknown>,
  locale: Locale,
  effectiveSchemas: EffectiveRecordPropertySchemasByCategory,
): EntityPropertyGroup[] =>
  metadataCategories.map((rc) => {
    const schemas = requireEffectiveRecordSchemas(effectiveSchemas, rc.category.id);
    return {
      categoryId: rc.category.id,
      categoryLabel: toCategoryChip(rc.category, locale).label,
      rows: schemas.map((schema) => ({
        position: schema.position,
        name: schema.metadataKey,
        value: properties[schema.metadataKey],
        propertyTypeId: schema.type,
        selectOptions: schema.selectOptions,
      })),
    };
  });

/** Properties not described by any category's schema (rendered as a raw JsonTable). */
const toOtherProperties = (
  metadataCategories: readonly MetadataCategory[],
  properties: Record<string, unknown>,
  effectiveSchemas: EffectiveRecordPropertySchemasByCategory,
): Record<string, unknown> => {
  const known = new Set(
    metadataCategories.flatMap((rc) =>
      requireEffectiveRecordSchemas(effectiveSchemas, rc.category.id).map((schema) => schema.metadataKey),
    ),
  );
  return Object.fromEntries(Object.entries(properties).filter(([key]) => !known.has(key)));
};

export function toEntityDetailUI(
  raw: EntityDetailRaw,
  compoundEntityRaw: RelatedCompoundEntityRaw | undefined,
  locale: Locale,
  recordGeoItems: readonly EntityGeoItemRead[] = [],
  effectiveSchemas: EffectiveRecordPropertySchemasByCategory,
): EntityDetailUI {
  const properties = (raw.properties ?? {}) as Record<string, unknown>;
  return {
    id: raw.id,
    displayName: displayName({ name: raw.name, nameJa: raw.nameJa }, locale),
    dateRange: formatDateRange(raw.date, raw.endDate),
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
    deactivatedAt: raw.deactivatedAt ?? null,
    isDeactivated: Boolean(raw.deactivatedAt),
    categories: toCategoryChips(raw.recordMetadata.recordMetadataCategories, locale),
    propertyGroups: toPropertyGroups(raw.recordMetadata.recordMetadataCategories, properties, locale, effectiveSchemas),
    otherProperties: toOtherProperties(raw.recordMetadata.recordMetadataCategories, properties, effectiveSchemas),
    metadata: raw.recordMetadata,
    geoItems: toEntityGeoItemRows(recordGeoItems, locale),
    sourceRelations: (raw.entityRecord?.sourceRecordEdges ?? []).map((e) => toSourceRelationRow(e, locale)),
    targetRelations: (raw.entityRecord?.targetRecordEdges ?? []).map((e) => toTargetRelationRow(e, locale)),
    compoundEntity: toRelatedCompoundEntity(compoundEntityRaw, locale),
  };
}

/**
 * Build the dateJdn/endDateJdn overlap filter for a GDTF range.
 * Ported verbatim from V1 `~/models/records/searchProps` (pure logic).
 */
const buildGdtfRangeWhere = (gdtfFrom?: string, gdtfTo?: string): RecordsBoolExp[] => {
  const parsed = parseGdtfRange(gdtfFrom, gdtfTo);
  if (!parsed.hasFilter || parsed.inverted) return [];

  const conditions: RecordsBoolExp[] = [{ dateJdn: { _isNull: false } }];
  if (parsed.toJdn != null) {
    conditions.push({ dateJdn: { _lte: parsed.toJdn } });
  }
  if (parsed.fromJdn != null) {
    conditions.push({
      _or: [
        { endDateJdn: { _gte: parsed.fromJdn } },
        { _and: [{ endDateJdn: { _isNull: true } }, { dateJdn: { _gte: parsed.fromJdn } }] },
      ],
    });
  }
  return conditions;
};

/** Translate the UI filter shape into a Hasura `RecordsBoolExp`. Ported from V1. */
export function buildEntitiesWhere(filters: EntityListFilters): RecordsBoolExp {
  const andList: RecordsBoolExp[] = [];

  if (filters.idOrName) {
    andList.push(
      validate(filters.idOrName) ? { id: { _eq: filters.idOrName } } : { name: { _like: `%${filters.idOrName}%` } },
    );
  }
  if (filters.referenceIds.length > 0) {
    andList.push({
      changeRequestItemRecords: {
        changeRequestItem: {
          changeRequest: {
            evidenceChangeRequests: {
              evidence: { referenceId: { _in: filters.referenceIds } },
            },
          },
        },
      },
    });
  }
  if (filters.categoryIds.length > 0) {
    andList.push({
      recordMetadata: { recordMetadataCategories: { categoryId: { _in: filters.categoryIds } } },
    });
  }
  if (filters.excludeDeactivated) {
    andList.push({ deactivatedAt: { _isNull: true } });
  }
  andList.push(...buildGdtfRangeWhere(filters.gdtfFrom, filters.gdtfTo));

  return { _and: andList };
}

const orderValue = (direction: SortDirection): OrderBy => (direction === "asc" ? "ASC" : "DESC");

/** Build a `RecordsOrderBy` from the sort column + direction. */
function buildEntitiesOrderBy(sortOrder: EntitySortOrder, direction: SortDirection): RecordsOrderBy {
  return { [sortOrder]: orderValue(direction) };
}

/** Assemble the full `GetEntities` query variables for a page of the list. */
export function buildEntitiesVariables(args: {
  tenantId: string;
  filters: EntityListFilters;
  sortOrder: EntitySortOrder;
  direction: SortDirection;
  pageIndex: number;
  pageSize: number;
}): GetEntitiesQueryVariables {
  const { tenantId, filters, sortOrder, direction, pageIndex, pageSize } = args;
  return {
    tenantId,
    limit: pageSize,
    offset: pageIndex * pageSize,
    where: {
      ...buildEntitiesWhere(filters),
      // Restrict to entity records (exclude relations) via an inner-join style filter.
      entityRecord: {},
    },
    order_by: buildEntitiesOrderBy(sortOrder, direction),
  };
}

// ── Detail: change requests / evidence / lineage ──────────────────────────────

const deriveChangeRequestItems = (raw: EntityDetailRaw) =>
  raw.changeRequestItemRecords
    .map((r) => r.changeRequestItem)
    .filter((ci): ci is NonNullable<typeof ci> => Boolean(ci));

const userName = (user: { displayName?: string | null; email?: string | null } | null | undefined): string =>
  user?.displayName || user?.email || "";

/** Change-request items for the detail Change Requests tab (all items, newest first). */
export function toEntityChangeRequestRows(raw: EntityDetailRaw): EntityChangeRequestRow[] {
  return deriveChangeRequestItems(raw).map((ci) => ({
    id: ci.id,
    createdAt: ci.createdAt,
    operationType: ci.operationType,
    properties: ci.properties,
    changeRequestId: ci.changeRequest.id,
    description: ci.changeRequest.description,
    userName: userName(ci.changeRequest.user),
    context: ci.changeRequest.context,
  }));
}

/** The non-rejected change requests reachable from this record (deduped). */
const liveChangeRequests = (raw: EntityDetailRaw) =>
  dedupeById(
    deriveChangeRequestItems(raw)
      .map((ci) => ci.changeRequest)
      .filter((cr) => cr.statusId !== "rejected"),
  );

/** One row per evidence, keyed on the first change request that names it: its own metadata, properties from the version that request pinned. */
const composedEvidences = (raw: EntityDetailRaw) =>
  dedupeById(
    liveChangeRequests(raw).flatMap((cr) =>
      cr.evidenceChangeRequests.map((ecr) => ({ ...ecr.evidence, properties: ecr.evidenceVersion.properties })),
    ),
  );

/** Evidence rows for the detail EvidencesBox (deduped). */
export function toEntityEvidenceRows(raw: EntityDetailRaw): EntityEvidenceRow[] {
  return composedEvidences(raw).map((ev) => {
    const props = (ev.properties ?? {}) as Record<string, unknown>;
    const ref = ev.reference;
    return {
      id: ev.id,
      date: typeof props.date === "string" ? props.date : "",
      name: getEvidenceName({ properties: props }),
      reference: ref ? { id: ref.id, shortCode: ref.shortCode, title: ref.title } : null,
      userName: userName(ev.user),
      updatedAt: ev.updatedAt ?? null,
    };
  });
}

/** Build the detail lineage graph: record → change requests → evidence → references. */
export function toEntityLineage(
  raw: EntityDetailRaw,
  labels: { record: string; changeRequest: string },
): EntityLineage {
  const changeRequests = liveChangeRequests(raw);
  const evidences = composedEvidences(raw);
  const references = dedupeById(evidences.map((e) => e.reference));

  const nodes: LineageNode[] = [
    { id: raw.id, type: "entity", label: labels.record, subLabel: raw.id },
    ...changeRequests.map(
      (cr): LineageNode => ({
        id: cr.id,
        type: "changeRequest",
        label: labels.changeRequest,
        subLabel: cr.id,
        muted: NOT_YET_APPLIED.has(cr.statusId),
      }),
    ),
    ...evidences.map(
      (ev): LineageNode => ({
        id: ev.id,
        type: "evidence",
        label: getEvidenceName({ properties: (ev.properties ?? {}) as Record<string, unknown> }),
        subLabel: ev.id,
      }),
    ),
    ...references.map(
      (rf): LineageNode => ({
        id: rf.id,
        type: "reference",
        label: rf.title || rf.shortCode || rf.id,
        subLabel: rf.id,
      }),
    ),
  ];

  const edges: LineageEdge[] = [];
  const push = (
    source: string,
    target: string,
    type: LineageEdgeType,
    extra: Pick<LineageEdge, "projectID" | "changeRequestJobID" | "evidenceJobID"> = {},
  ) => edges.push({ id: `${source}->${target}`, source, target, type, ...extra });

  // Applied change requests connect to the record (not-yet-applied are omitted).
  for (const cr of changeRequests) {
    if (!NOT_YET_APPLIED.has(cr.statusId)) push(cr.id, raw.id, "changeRequestToRecord");
  }
  // Evidence → change request (click-through opens the batch-change-requests job peek).
  for (const cr of changeRequests) {
    const bcrJob = cr.batchChangeRequestsJobRun?.batchChangeRequestsJob;
    for (const ecr of cr.evidenceChangeRequests) {
      push(ecr.evidence.id, cr.id, "evidenceToChangeRequest", {
        projectID: bcrJob?.dataImportProjectId ?? undefined,
        changeRequestJobID: bcrJob?.id ?? undefined,
      });
    }
  }
  // Reference → evidence (click-through opens the batch-evidences job peek).
  for (const ev of evidences) {
    push(ev.reference.id, ev.id, "referenceToEvidence", {
      projectID: ev.batchEvidencesJob?.dataImportProjectId ?? undefined,
      evidenceJobID: ev.batchEvidencesJob?.id ?? undefined,
    });
  }

  const edgeMap = new Map(edges.map((e) => [e.id, e]));
  return { nodes, edges: [...edgeMap.values()] };
}

/**
 * Splits the entity's relations into what a deactivation would take down and what an activation
 * could bring back.
 *
 * The entity appears on both edge arrays when it is source and target of the same relation, so
 * the relations are deduplicated by id. An endpoint that is the entity itself is not counted
 * against the relation: after this entity is activated it will be live, and before it is
 * deactivated the relation is going down anyway.
 */
export const toEntityRelationLifecycle = (
  raw: EntityRelationLifecycleRaw | undefined,
  locale: Locale,
): EntityRelationLifecycle => {
  const entityId = raw?.id;
  const edges = [...(raw?.entityRecord?.sourceRecordEdges ?? []), ...(raw?.entityRecord?.targetRecordEdges ?? [])];

  const byRelationId = new Map<string, EntityRelationLifecycleRow>();
  for (const edge of edges) {
    const relation = edge.relationRecord;
    if (byRelationId.has(relation.record.id)) continue;

    const otherEndpoints = [...relation.sourceRecordEdges, ...relation.targetRecordEdges]
      .map((endpointEdge) => endpointEdge.entity)
      .filter((endpoint): endpoint is NonNullable<typeof endpoint> => endpoint != null && endpoint.id !== entityId);

    byRelationId.set(relation.record.id, {
      relationId: relation.record.id,
      displayName: displayName(relation.record, locale) || relation.record.id,
      isDeactivated: relation.record.deactivatedAt != null,
      blockedByEndpoint: otherEndpoints.some((endpoint) => endpoint.deactivatedAt != null),
    });
  }

  const rows = [...byRelationId.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
  return {
    toDeactivate: rows.filter((row) => !row.isDeactivated),
    activatable: rows.filter((row) => row.isDeactivated && !row.blockedByEndpoint),
  };
};
